/**
 * ★ Fase 44 — FUNNEL IT-AL (Italiano · Albania) — landing completa 9 sezioni.
 * URL: /f/it-al — slide cliente «landing 1..9» (vedi /public/funnels/it-al/MATERIALE.md)
 *
 * FLUSSO VIDEO (dal cliente):
 *  - PRE-VSL (GATE): j9_HhKF2xgA — cattura il lead; finché non lo cattura
 *    NON si procede nella landing (sezioni successive bloccate).
 *  - VSL REALE: oNrux7V3TzM — promuove il Verification Pass, subito sotto il gate.
 *  - VERIFICATION PASS: DEEz5eXicGU — mostra cosa faranno, prezzo €300, CTA forti.
 *  - TESTIMONIANZE: fonte unica lib/testimonials.js (13 video: 4 in evidenza + 9 aggiunte il 19/10/2026).
 *
 * NB: La Mar Residence, Vista Mare e Palm Paradise sono ELIMINATE
 * definitivamente (Fase 43.1 / 59). Asset Albania attivi dal DB: Narea e
 * Three Garden Qerret (★ Fase 64) → ogni card apre la landing dedicata
 * (/f/it-al-na, /f/it-al-tg) con form interno che riporta qui (?lead=ok).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import i18n from '../../../i18n';
import { api } from '../../../lib/api';
import { toast } from 'sonner';
import {
  ArrowRight, Landmark, BarChart3, Layers, MapPin, FileCheck2, Users,
  PlayCircle, Network, BedDouble, CalendarClock, Send, Loader2,
  CheckCircle2, Palmtree, Building2, ShieldCheck, Star, Circle,
  User, Mail, Phone, X,
} from 'lucide-react';
import FunnelChatWidget from '../../../components/FunnelChatWidget';
import { testimonialsFor, TESTIMONIALS_FEATURED_COUNT } from '../../../lib/testimonials';
import { setPageMeta, ogImageFor } from '../../../lib/seo';
import { IndonesiaAssetCards } from '../../../components/funnels/IndonesiaAssetCards';
import { VpVideoPlaylist } from '../../../components/funnels/VpVideoPlaylist'; // ★ 2026-10-22 — video VP per territorio (Albania: 3)
import { vpVideosFor } from '../../../lib/vpVideos';
import { useHashScroll } from '../../../lib/useHashScroll'; // ★ Fase 94 — /f/xx#verification dal bridge Three Garden
// ★ Fase 96 (Doc 02) — doppio ingresso + scelta guidata + attribution/eventi
import { captureAttribution, trackFunnelEvent } from '../../../lib/attribution';
import { HubGuidedChoice } from '../../../components/funnels/HubGuidedChoice';
import { NoObligationNote } from '../../../components/funnels/NoObligationNote';

const INK = '#081712';
const EMERALD = '#0C2B21';
const GOLD = '#D6B25E';
const GOLD_DEEP = '#B8860B';
const IVORY = '#F4EEE1';
const SAND = '#CFC6B2';

/* ★ Fase 49 — Sfondo UNICO per tutto il funnel (immagine cliente
   «Dallo spazio al resort», sfocata): spazio in alto → costa → resort in fondo.
   Le vecchie scene per-sezione (/funnels/it-al/scene-0N.jpg) non sono più usate. */

/* ── Video flow (definiti dal cliente) ── */
const PRE_VSL_ID = 'j9_HhKF2xgA';        // gate: cattura lead
const VSL_ID = 'oNrux7V3TzM';            // VSL reale → promuove Verification Pass
const VP_VIDEO_ID = 'DEEz5eXicGU';       // Verification Pass: cosa faranno
/* ★ Fase 59 — video per TERRITORIO sotto la lista paesi (richiesta cliente):
   Albania → video VP Albania · Indonesia → HCF Lombok Journey · Thailandia →
   video dedicato Thailandia. */
const TERRITORY_VIDEOS = {
  AL: { id: 'DEEz5eXicGU', title: 'Verification Pass Albania — cosa faremo' },
  ID: { id: 'V0Z643ZA4Pg', title: 'HCF — The Lombok Journey' },
  TH: { id: 'VvZqwA1ZSOU', title: 'HCF — Thailandia' },
};
// ★ 2026-10-19 — fonte unica (13 video) in lib/testimonials.js: 4 in evidenza + le altre a richiesta
const TESTIMONIALS = testimonialsFor('it');
const TESTIMONIALS_FEATURED = TESTIMONIALS.slice(0, TESTIMONIALS_FEATURED_COUNT);
const TESTIMONIALS_MORE = TESTIMONIALS.slice(TESTIMONIALS_FEATURED_COUNT);

const GATE_KEY = 'hcf_ital_gate_ok'; // legacy (landing ora unica, senza blocco)

/* ★ Fase 46 — Calendly diretto di Richard Demichele (CEO).
   Valore live letto da GET /public/funnel/it-al (booking_url); questo è il fallback. */
const CALENDLY_RICHARD = 'https://calendly.com/legal-housecashflows/colloquio-strategico-con-il-ceo-di-hcf-group';

/* ── ★ Fase 47 — Date ufficiali Verification Pass (weekend · €300) ──
   24 posti totali per data; `left` = posti ancora disponibili (dal cliente). */
const VP_DATES = [
  { id: 'ott', label: '23–25 Ottobre', start: '2026-10-23', total: 24, left: 14 },
  { id: 'nov1', label: '6–8 Novembre', start: '2026-11-06', total: 24, left: 18 },
  { id: 'nov2', label: '20–22 Novembre', start: '2026-11-20', total: 24, left: 22 },
  { id: 'dic', label: '4–6 Dicembre', start: '2026-12-04', total: 24, left: 18 },
];

/* ★ Fase 47 — Extra Pass Private (infrasettimanale · €500 · max 5 investitori) */
const VP_EXTRA_DATES = [
  { id: 'ott-x', label: '27–29 Ottobre', start: '2026-10-27', max: 5 },
  { id: 'nov1-x', label: '10–12 Novembre', start: '2026-11-10', max: 5 },
  { id: 'nov2-x', label: '24–26 Novembre', start: '2026-11-24', max: 5 },
  { id: 'dic-x', label: '15–17 Dicembre', start: '2026-12-15', max: 5 },
];

/* ── ★ Fase 52 — Verification Pass multi-paese sulla stessa landing ──
   Indonesia: €5.000 · 9 giorni / 7 notti · 12 posti · una partenza ogni
   14 giorni dal 15 ottobre (la prima è già piena).
   Thailandia: €3.500 · stesse date · SOLD OUT (non selezionabile).
   Fallback locali: la fonte primaria resta il CRM via /public/funnel/it-al. */
const VP_DATES_ID = [
  { id: 'id-1', label: '15–23 Ottobre', start: '2026-10-15', total: 12, left: 0 },
  { id: 'id-2', label: '29 Ott – 6 Nov', start: '2026-10-29', total: 12, left: 4 },
  { id: 'id-3', label: '12–20 Novembre', start: '2026-11-12', total: 12, left: 7 },
  { id: 'id-4', label: '26 Nov – 4 Dic', start: '2026-11-26', total: 12, left: 9 },
  { id: 'id-5', label: '10–18 Dicembre', start: '2026-12-10', total: 12, left: 11 },
];

const VP_COUNTRIES = {
  AL: {
    name: 'Albania', price: '€300', rateLabel: 'Private Rate · Weekend', eventDays: 3,
    desc: 'Accesso completo ai dati, analisi del territorio e opportunità selezionate nel mercato albanese.',
    lockCta: 'Blocca il Private Rate €300',
  },
  ID: {
    name: 'Indonesia', price: '€5.000', rateLabel: 'Private Rate · 9 giorni · 7 notti', eventDays: 9,
    desc: 'Verifica estesa sul campo: 9 giorni e 7 notti tra asset, cantieri e territorio nel mercato indonesiano.',
    lockCta: 'Blocca il Private Rate €5.000',
  },
  TH: {
    name: 'Thailandia', price: '€3.500', rateLabel: 'Private Rate · 9 giorni · 7 notti', eventDays: 9,
    desc: 'Verifica estesa sul campo: 9 giorni e 7 notti tra asset e territorio nel mercato thailandese.',
    lockCta: "Entra in lista d'attesa",
    soldOut: true,
  },
};

/* Thailandia: stesse date di Indonesia ma tutte sold out (fallback locale) */
const VP_DATES_TH = VP_DATES_ID.map((d) => ({ ...d, id: d.id.replace('id-', 'th-'), left: 0 }));

/* ── ★ Fase 55 — Card prodotto Indonesia (compatte, a sinistra) ──
   Ogni card apre la mini-landing dedicata su pagina a sé
   (/f/it-bali, /f/it-lombok), riutilizzabile nelle campagne dirette. */
const INDONESIA_CARDS = [
  {
    slug: 'villa-flora', place: 'Bali · Indonesia', name: 'Villa Flora',
    tagline: 'Villa intima con piscina privata, interni luminosi e design boho-minimal. Un angolo privato e tropicale.',
    facts: ['Piscina privata', 'Interni luminosi', 'Design boho-minimal'],
    cover: '/funnels/indonesia/villa-flora/card-cover.webp', coverPosition: 'center 40%',
    href: '/f/it-ba',
  },
  {
    slug: 'green-paradise-lombok', place: 'Lombok · Bumbang Bay', name: 'Green Paradise',
    tagline: 'Resort eco-luxury fronte oceano con spiaggia privata: 32 ville private gestite come resort.',
    facts: ['32 ville private', 'Fronte oceano', 'Apertura ott. 2027'],
    cover: '/funnels/indonesia/green-paradise-lombok/photos/gp-cover.webp', coverPosition: 'center 45%',
    href: '/f/it-lo',
  },
];

/* ── ★ Fase 64 — Landing dedicate per asset Albania (form interno) ── */
const ASSET_LANDING_HREF = {
  'narea': '/f/it-al-na',
  'three-garden-qerret': '/f/it-al-tg',
};

/* ── ★ Fase 47 — Countdown AUTOMATICO sulla prossima data VP ──
   Si azzera da solo: quando una data è passata (3 giorni di evento),
   il countdown riparte automaticamente verso la data successiva.
   ★ Fase 48 — le date arrivano dal CRM (prop `dates`). */
function useVpCountdown(dates, eventDays = 3) {
  const calc = () => {
    const list = dates && dates.length ? dates : VP_DATES;
    const now = Date.now();
    for (const d of list) {
      const startT = new Date(`${d.start}T09:00:00`).getTime();
      const endT = startT + eventDays * 86400000; // durata evento (3 gg weekend, 9 gg esteso)
      if (now < startT) return { date: d, left: startT - now, running: false };
      if (now < endT) return { date: d, left: 0, running: true };
    }
    const last = list[list.length - 1];
    return { date: last, left: 0, running: false };
  };
  const [st, setSt] = useState(calc);
  useEffect(() => {
    setSt(calc());
    const id = setInterval(() => setSt(calc()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates]);
  const s = Math.floor(st.left / 1000);
  return {
    date: st.date, running: st.running,
    d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600),
    m: Math.floor((s % 3600) / 60), s: s % 60,
  };
}

/* ── Countdown: vedi useVpCountdown (★ Fase 47, date reali con reset automatico) ── */

/* ── ★ Fase 47 — Barra di avanzamento scroll (sottile, oro) ── */
function ScrollProgress() {
  const [p, setP] = useState(0);
  useEffect(() => {
    let raf = 0;
    const update = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      setP(max > 0 ? Math.min(1, window.scrollY / max) : 0);
      raf = 0;
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return (
    <div className="h-[3px] w-full overflow-hidden" style={{ background: `${GOLD}1F` }} aria-hidden="true">
      <div
        className="h-full w-full origin-left"
        style={{
          transform: `scaleX(${p})`,
          background: `linear-gradient(90deg, ${GOLD_DEEP}, ${GOLD})`,
          boxShadow: `0 0 10px ${GOLD}66`,
          transition: 'transform 80ms linear',
        }}
        data-testid="ital-scroll-progress"
      />
    </div>
  );
}

const Kicker = ({ children }) => (
  <div className="flex items-center gap-3 text-[11px] md:text-xs font-sans font-semibold tracking-[0.3em] uppercase" style={{ color: SAND }}>
    {children}
    <span className="inline-block w-10 border-t" style={{ borderColor: GOLD }} />
  </div>
);

const IvoryPill = ({ children, onClick, testId, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center justify-center gap-2 rounded-full w-full sm:w-auto px-6 sm:px-8 py-3.5 text-[12px] sm:text-[13px] text-center font-sans font-bold tracking-[0.14em] sm:tracking-[0.2em] uppercase transition-transform duration-200 hover:scale-[1.02] active:scale-[0.99] ${className}`}
    style={{ background: IVORY, color: '#143C2B', boxShadow: `0 0 0 1px ${GOLD}, 0 8px 30px rgba(0,0,0,0.35)` }}
    data-testid={testId}
  >
    {children}<ArrowRight className="w-4 h-4" />
  </button>
);

const YT = ({ id, title, autoplay = false }) => (
  <iframe
    src={`https://www.youtube.com/embed/${id}?rel=0&modestbranding=1${autoplay ? '&autoplay=1' : ''}`}
    title={title}
    className="w-full h-full"
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
    allowFullScreen
  />
);

/* ── ★ Fase 44.4 — Form restyling: campi "dark glass" + oro (palette funnel) ── */
const LUX_CSS = `
  .ital-lux-field { background: rgba(244,238,225,0.07); border: 1px solid rgba(214,178,94,0.30); color: #F4EEE1; caret-color: #D6B25E; transition: border-color .2s, box-shadow .2s, background-color .2s; }
  .ital-lux-field::placeholder { color: rgba(207,198,178,0.55); }
  .ital-lux-field:hover:not(:focus) { border-color: rgba(214,178,94,0.55); }
  .ital-lux-field:focus { border-color: #D6B25E; background: rgba(244,238,225,0.12); box-shadow: 0 0 0 3px rgba(214,178,94,0.20); outline: none; }
  .ital-gold-btn { background: linear-gradient(135deg, #E3C87A 0%, #D6B25E 45%, #B8860B 100%); color: #11271E; box-shadow: 0 10px 30px rgba(214,178,94,0.22), inset 0 1px 0 rgba(255,255,255,0.35); transition: transform .2s, box-shadow .2s; }
  .ital-gold-btn:hover:not(:disabled) { transform: scale(1.015); box-shadow: 0 14px 42px rgba(214,178,94,0.34), inset 0 1px 0 rgba(255,255,255,0.4); }
  .ital-gold-btn:active:not(:disabled) { transform: scale(0.99); }
  .ital-gold-btn:disabled { opacity: 0.5; }
`;

function LuxField({ icon: Icon, testId, ...props }) {
  return (
    <div className="relative">
      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: GOLD }} strokeWidth={1.6} />
      <input
        {...props}
        data-testid={testId}
        className="ital-lux-field w-full h-12 rounded-xl pl-11 pr-4 text-sm font-sans"
      />
    </div>
  );
}

/* Sezione del funnel — ★ Fase 49: trasparente, lo sfondo è unico a livello
   di pagina (immagine sfocata «Dallo spazio al resort»). */
function Scene({ n, children, minH = 'min-h-0', id }) {
  return (
    <section id={id} className={`relative ${minH} flex`} data-testid={`ital-section-${n}`}>
      <div className="relative w-full max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16 flex flex-col">
        {children}
      </div>
    </section>
  );
}

/* ── ★ Fase 47 — Exit intent: ultimo invito (Calendly) prima di uscire ── */
function ExitIntentModal({ calendly }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onOut = (e) => {
      if (e.clientY > 10 || e.relatedTarget) return; // solo uscita dal bordo alto
      try {
        if (sessionStorage.getItem('hcf_ital_exit_shown') === '1') return;
        sessionStorage.setItem('hcf_ital_exit_shown', '1');
      } catch { /* no-op */ }
      setShow(true);
    };
    const onKey = (e) => e.key === 'Escape' && setShow(false);
    document.addEventListener('mouseout', onOut);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mouseout', onOut);
      document.removeEventListener('keydown', onKey);
    };
  }, []);
  if (!show) return null;
  return (
    <div
      className="fixed inset-0 z-[90] grid place-items-center p-4"
      style={{ background: 'rgba(4,10,8,0.72)', backdropFilter: 'blur(10px)' }}
      onClick={() => setShow(false)}
      data-testid="ital-exit-modal"
    >
      <div
        className="relative w-full max-w-md rounded-2xl border p-7 md:p-8 text-center font-serif"
        style={{ borderColor: `${GOLD}77`, background: `linear-gradient(160deg, ${EMERALD}F8, ${INK}F5)`, boxShadow: '0 30px 90px rgba(0,0,0,0.6)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button" onClick={() => setShow(false)} aria-label="Chiudi"
          className="absolute top-3.5 right-3.5 w-8 h-8 rounded-full grid place-items-center border transition-colors duration-150 hover:bg-white/10"
          style={{ borderColor: `${GOLD}55`, color: SAND }}
          data-testid="ital-exit-close"
        >
          <X className="w-4 h-4" />
        </button>
        <img src="/funnels/hcf-logo.png" alt="HCF Property" className="h-10 mx-auto" />
        <div className="mt-4 text-[11px] font-sans font-bold tracking-[0.3em] uppercase" style={{ color: GOLD }}>Prima di andare</div>
        <h3 className="mt-2 text-2xl md:text-3xl leading-snug" style={{ color: IVORY }}>
          Un&rsquo;ultima cosa:<br /><span style={{ color: GOLD }}>parliamone 15 minuti</span>
        </h3>
        <p className="mt-3 text-sm font-sans leading-relaxed" style={{ color: SAND }}>
          Nessun obbligo di acquisto: una <b style={{ color: IVORY }}>consulenza strategica
          col CEO</b> di HCF Group, Richard Demichele, per capire
          se il percorso Albania fa per te.
        </p>
        <a
          href={calendly} target="_blank" rel="noopener noreferrer"
          className="ital-gold-btn mt-6 w-full rounded-full min-h-12 py-2.5 px-4 text-[11px] sm:text-[12px] font-sans font-bold tracking-[0.12em] sm:tracking-[0.2em] uppercase text-center leading-snug inline-flex items-center justify-center gap-2"
          data-testid="ital-exit-calendly"
        >
          <CalendarClock className="w-4 h-4" /> Prenota una consulenza strategica col CEO
        </a>
        <button
          type="button" onClick={() => setShow(false)}
          className="mt-3 w-full text-[12px] font-sans tracking-wide underline-offset-4 hover:underline"
          style={{ color: SAND }}
          data-testid="ital-exit-continue"
        >
          Continua a leggere la pagina
        </button>
      </div>
    </div>
  );
}

/* Card video testimonianza: thumbnail → play inline */
function TestimonialCard({ id, label, name }) {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="rounded-xl overflow-hidden border" style={{ borderColor: `${GOLD}55`, background: `${EMERALD}B3` }} data-testid={`ital-testimonial-${id}`}>
      <div className="aspect-video relative">
        {playing ? (
          <YT id={id} title={label} autoplay />
        ) : (
          <button type="button" onClick={() => setPlaying(true)} className="group absolute inset-0 w-full" aria-label={`Guarda: ${label}`} data-testid={`ital-testimonial-play-${id}`}>
            <img src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`} alt={label} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <span className="absolute inset-0" style={{ background: `${INK}59` }} />
            <span className="absolute inset-0 grid place-items-center">
              <span className="w-14 h-14 rounded-full grid place-items-center border transition-transform duration-200 group-hover:scale-110" style={{ borderColor: GOLD, background: `${INK}B3` }}>
                <PlayCircle className="w-8 h-8" style={{ color: GOLD }} strokeWidth={1.2} />
              </span>
            </span>
          </button>
        )}
      </div>
      <div className="px-4 py-3">
        <div className="font-serif text-base" style={{ color: IVORY }}>{label}</div>
        {name && <div className="mt-1 text-xs font-sans tracking-wide" style={{ color: GOLD }} data-testid={`ital-testimonial-name-${id}`}>{name}</div>}
        <div className="mt-1.5 w-8 border-t" style={{ borderColor: GOLD }} />
      </div>
    </div>
  );
}

export default function FunnelITAL() {
  const [assets, setAssets] = useState([]);
  const [showAllTestimonials, setShowAllTestimonials] = useState(false); // ★ 2026-10-19
  const [calendly, setCalendly] = useState(CALENDLY_RICHARD);
  // ★ Fase 48 — date/posti gestiti dal CRM (fallback = costanti locali)
  const [vpDates, setVpDates] = useState(VP_DATES);
  const [extraDates, setExtraDates] = useState(VP_EXTRA_DATES);
  // ★ Fase 52 — più paesi sulla stessa landing (AL default, ID e TH selezionabili)
  const [territory, setTerritory] = useState('AL');
  const [vpDatesID, setVpDatesID] = useState(VP_DATES_ID);
  const [vpDatesTH, setVpDatesTH] = useState(VP_DATES_TH);
  // ★ Fase 54 — card prodotto per territorio (contenuti in arrivo dal cliente)
  const [assetsID, setAssetsID] = useState([]);
  const [assetsTH, setAssetsTH] = useState([]);
  // Countdown ID: punta alla prossima data CON posti disponibili
  const vpDatesIDOpen = useMemo(() => {
    const open = vpDatesID.filter((d) => (d.left ?? 0) > 0);
    return open.length ? open : vpDatesID;
  }, [vpDatesID]);
  const vpAL = useVpCountdown(vpDates, VP_COUNTRIES.AL.eventDays);
  const vpID = useVpCountdown(vpDatesIDOpen, VP_COUNTRIES.ID.eventDays);
  const vpTH = useVpCountdown(vpDatesTH, VP_COUNTRIES.TH.eventDays);
  const vp = territory === 'ID' ? vpID : territory === 'TH' ? vpTH : vpAL;
  const vpCountry = VP_COUNTRIES[territory] || VP_COUNTRIES.AL;
  const vpActiveDates = territory === 'ID' ? vpDatesID : territory === 'TH' ? vpDatesTH : vpDates;

  // Form hero (video + contatti, ★ Fase 46: landing unica senza gate)
  const [gName, setGName] = useState('');
  const [gEmail, setGEmail] = useState('');
  const [gPhone, setGPhone] = useState('');
  const [gSending, setGSending] = useState(false);
  // ★ Fase 64 — se arrivo da una landing dedicata (/f/it-al-na, /f/it-al-tg)
  // con ?lead=ok il lead è GIÀ stato catturato: mostro subito la CTA Calendly.
  const [searchParams] = useSearchParams();
  const [gSent, setGSent] = useState(searchParams.get('lead') === 'ok');
  // ★ Fase 68 — provenienza (it-al-na → brochure Narea inviata via email)
  const leadFrom = searchParams.get('from') || '';
  const fromNarea = leadFrom === 'it-al-na';
  const fromThreeGarden = leadFrom === 'it-al-tg';
  const brochureName = fromNarea ? 'Narea' : fromThreeGarden ? 'Three Garden' : '';

  // Form finale (dossier)
  const [fName, setFName] = useState('');
  const [fEmail, setFEmail] = useState('');
  const [fPhone, setFPhone] = useState('');
  const [fSending, setFSending] = useState(false);
  const [fSent, setFSent] = useState(false);
  // ★ Fase 96 (Doc 02) — ingresso aperto nella scelta guidata (la CTA primaria dell'hero apre «projects»)
  const [guideEntry, setGuideEntry] = useState(null);

  useEffect(() => {
    if (i18n.language !== 'it') i18n.changeLanguage('it');
    captureAttribution(); // ★ Fase 91/96 — utm_*, click_id, first_touch, landing_path preservati
    // ★ Fase 89 — meta social (title/description/og:image/og:url) del funnel IT
    setPageMeta({
      title: 'HCF Property · Albania — Dal mattone al patrimonio',
      description: 'HCF Property — Dal mattone al patrimonio. Verification Pass Albania: verifica territorio, asset e dati dal vivo, prima di investire. Prenota la call con il nostro CEO.',
      image: ogImageFor('it'),
      imageAlt: 'HCF Property — Dal mattone al patrimonio. Verification Pass Albania.',
      path: '/f/it',
      locale: 'it',
    });
    api.get('/public/funnel/it').then((r) => {
      setAssets(r.data?.properties || []);
      if (r.data?.booking_url) setCalendly(r.data.booking_url);
      // ★ Fase 48 — date live dal CRM (normalizzate sui campi usati in UI)
      if (Array.isArray(r.data?.vp_dates) && r.data.vp_dates.length) {
        setVpDates(r.data.vp_dates.map((d) => ({
          id: d.id, label: d.label, start: d.start,
          total: d.total ?? 24, left: d.left ?? 0,
        })));
      }
      if (Array.isArray(r.data?.vp_extra_dates) && r.data.vp_extra_dates.length) {
        setExtraDates(r.data.vp_extra_dates.map((d) => ({
          id: d.id, label: d.label, start: d.start, max: d.max_people ?? 5,
        })));
      }
      // ★ Fase 52 — date Indonesia live dal CRM (funnel code IT-ID)
      if (Array.isArray(r.data?.vp_dates_id) && r.data.vp_dates_id.length) {
        setVpDatesID(r.data.vp_dates_id.map((d) => ({
          id: d.id, label: d.label, start: d.start,
          total: d.total ?? 12, left: d.left ?? 0,
        })));
      }
      // ★ Fase 54 — date Thailandia + card prodotto per territorio
      if (Array.isArray(r.data?.vp_dates_th) && r.data.vp_dates_th.length) {
        setVpDatesTH(r.data.vp_dates_th.map((d) => ({
          id: d.id, label: d.label, start: d.start,
          total: d.total ?? 12, left: d.left ?? 0,
        })));
      }
      if (Array.isArray(r.data?.properties_id)) setAssetsID(r.data.properties_id);
      if (Array.isArray(r.data?.properties_th)) setAssetsTH(r.data.properties_th);
    }).catch(() => {});
  }, []);

  useHashScroll(); // ★ Fase 94 (Doc 02) — scroll alla sezione nell'hash (es. #verification)
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  // ★ Fase 96 — CTA primaria hero → apre la scelta guidata (step reale: orientamento, non vendita)
  const openGuide = (label) => {
    trackFunnelEvent('cta_click', { funnel: 'it', label, target: '#percorso-guidato' });
    setGuideEntry('projects');
    // scroll dopo il render del pannello
    window.setTimeout(() => scrollTo('percorso-guidato'), 50);
  };
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const submitHero = async (e) => {
    e?.preventDefault();
    if (gName.trim().length < 2 || !emailRe.test(gEmail.trim()) || gSending) return;
    setGSending(true);
    try {
      await api.post('/public/funnel-lead', {
        funnel: 'it', name: gName.trim(), email: gEmail.trim(),
        phone: gPhone.trim() || null, message: 'Richiesta dossier Albania (form hero)',
      });
      try { localStorage.setItem(GATE_KEY, '1'); } catch { /* no-op */ }
      setGSent(true);
      toast.success('Richiesta inviata!', { description: 'Riceverai il dossier: un Senior Associate ti contatterà a breve.' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invio non riuscito, riprova.');
    } finally {
      setGSending(false);
    }
  };

  const submitFinal = async (e) => {
    e?.preventDefault();
    if (fName.trim().length < 2 || !emailRe.test(fEmail.trim()) || fSending) return;
    setFSending(true);
    try {
      await api.post('/public/funnel-lead', {
        funnel: 'it', name: fName.trim(), email: fEmail.trim(),
        phone: fPhone.trim() || null,
        message: territory === 'TH'
          ? "LISTA D'ATTESA — Verification Pass Thailandia (€3.500)"
          : `Richiesta dossier / Verification Pass ${vpCountry.name} (${vpCountry.price})`,
      });
      setFSent(true);
      toast.success('Richiesta inviata!', { description: 'Un Senior Associate ti contatterà a breve.' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invio non riuscito, riprova.');
    } finally {
      setFSending(false);
    }
  };

  /* Asset card (slide 06) — override tagline dalle slide; La Mar esclusa */
  const assetCopy = {
    'narea': 'Nuova visione sulla costa albanese',
    'three-garden-qerret': 'Fronte mare a Qerret: posizione, percezione e valore',
    'green-paradise-lombok': 'Eco-luxury resort fronte oceano a Lombok',
  };
  /* ★ Fase 64 — card Albania → landing dedicata con form interno
     (tag IT-AL-NA / IT-AL-TG → redirect qui). Fallback: scheda resort. */
  const assetHref = (slug) => ASSET_LANDING_HREF[slug] || `/it/resort/${slug}`;

  /* ★ Fase 54 — card prodotto coerenti col territorio selezionato
     (contenuti definitivi in arrivo dal cliente; per ora dal CRM). */
  const sceneAssets = territory === 'ID' ? assetsID : territory === 'TH' ? assetsTH : assets;

  return (
    <div className="relative min-h-screen font-serif" style={{ background: INK, color: IVORY }} data-testid="funnel-page-it">
      <style>{LUX_CSS}</style>
      {/* ★ Fase 49 — sfondo unico SCORREVOLE per l'intero funnel:
          immagine verticale nitida (0 blur, asset upscalato 3x) che accompagna
          lo scroll — narrativa «dallo spazio → costa → resort» — con overlay
          scuro leggero + vignettatura per la leggibilità dei testi. */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }} aria-hidden="true">
        <img
          src="/funnels/bg-albania.webp" alt=""
          className="w-full h-full object-cover object-center"
          style={{ imageRendering: 'auto' }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(180deg, ${INK}BF 0%, ${INK}80 12%, ${INK}73 50%, ${INK}80 88%, ${INK}C9 100%)`,
          }}
        />
      </div>
      {/* ── Header sticky ── */}
      <header className="sticky top-0 z-40" style={{ background: `${INK}E8`, backdropFilter: 'blur(14px)' }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <img src="/funnels/hcf-logo.png" alt="HCF Property" className="h-9 w-auto" />
          <a
            href={calendly}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center rounded-full h-9 px-5 text-[11px] font-sans font-bold tracking-[0.18em] uppercase transition-transform duration-150 hover:scale-[1.03]"
            style={{ background: GOLD, color: '#143C2B' }}
            data-testid="ital-header-cta"
          >
            <span className="hidden sm:inline">Prenota una consulenza strategica col CEO</span>
            <span className="sm:hidden">Consulenza col CEO</span>
          </a>
        </div>
        {/* ★ Fase 46.7 — barra di avanzamento della landing */}
        <ScrollProgress />
      </header>

      {/* ════════ 01/09 — DAL MATTONE AL PATRIMONIO + VIDEO 5 MIN + FORM ════════
          ★ Fase 46 — landing UNICA: video e form di contatto a fianco della
          pagina 1, nessun blocco: il percorso prosegue subito sotto. */}
      <Scene n={1} id="top" minH="min-h-[92vh]">
        <div className="my-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center pt-10 pb-6">
          {/* Colonna sinistra — copy pagina 1 */}
          <div className="max-w-2xl">
            <h1 className="text-[2.6rem] sm:text-5xl md:text-6xl xl:text-7xl leading-[1.02] tracking-tight" data-testid="ital-hero-title">
              <span style={{ color: IVORY }}>Dal mattone</span><br />
              <span style={{ color: GOLD }}>al patrimonio</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl leading-relaxed font-sans" style={{ color: IVORY }}>
              Non vendiamo solo immobili.<br className="hidden md:block" />{' '}
              Costruiamo infrastrutture patrimoniali:<br className="hidden md:block" />{' '}
              asset, cash flow, organizzazione fiscale<br className="hidden md:block" />{' '}
              e continuità nel tempo.
            </p>
            <div className="mt-6 w-12 border-t-2" style={{ borderColor: GOLD }} />
            <p className="mt-5 text-base md:text-lg leading-relaxed" style={{ color: SAND }}>
              Prima capisci il sistema.<br />Poi scegli il territorio.<br />Poi verifichi l&rsquo;asset.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap gap-3">
              {/* ★ Fase 96 (Doc 02) — CTA primaria testuale: apre la scelta guidata (#percorso-guidato) */}
              <IvoryPill onClick={() => openGuide('hero_primary')} testId="ital-hero-cta" className="!normal-case !tracking-normal !text-[14px] sm:!text-[15px] !font-semibold !py-3 text-left">
                Scopri quale percorso HCF è coerente con i tuoi obiettivi
              </IvoryPill>
              {/* CTA storica mantenuta come secondaria (nessuna cannibalizzazione) */}
              <button
                type="button" onClick={() => scrollTo('ecosistema')}
                className="inline-flex items-center justify-center gap-2 rounded-full w-full sm:w-auto px-6 py-3.5 text-[12px] sm:text-[13px] font-sans font-bold tracking-[0.14em] sm:tracking-[0.2em] uppercase border transition-colors duration-150 hover:bg-white/5"
                style={{ borderColor: GOLD, color: IVORY }}
                data-testid="ital-hero-secondary"
              >
                Scopri come funziona <PlayCircle className="w-4 h-4" />
              </button>
            </div>
            <NoObligationNote lang="it" theme="dark" className="mt-3 !justify-start" testId="ital-hero-no-obligation" compact />
            <div className="mt-9 flex flex-wrap items-center gap-x-0 gap-y-3">
              {[{ icon: Landmark, label: 'Asset' }, { icon: BarChart3, label: 'Cash Flow' }, { icon: Layers, label: 'Struttura' }].map((it, i) => (
                <React.Fragment key={it.label}>
                  {i > 0 && <span className="hidden sm:block h-8 w-px mx-5" style={{ background: `${GOLD}55` }} />}
                  <span className="inline-flex items-center gap-2.5 mr-5 sm:mr-0">
                    <it.icon className="w-5 h-5" style={{ color: GOLD }} strokeWidth={1.6} />
                    <span className="text-sm md:text-base" style={{ color: IVORY }}>{it.label}</span>
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Colonna destra — video (5 min) + form adattato */}
          <div className="w-full max-w-xl lg:ml-auto space-y-4" data-testid="ital-gate-section">
            <div className="flex items-center gap-3 text-[11px] md:text-xs font-sans font-semibold tracking-[0.3em] uppercase" style={{ color: SAND }}>
              <PlayCircle className="w-4 h-4" style={{ color: GOLD }} />
              Il sistema HCF in 5 minuti
              <span className="inline-block flex-1 border-t" style={{ borderColor: `${GOLD}66` }} />
            </div>
            <div className="rounded-2xl overflow-hidden border" style={{ borderColor: `${GOLD}66`, boxShadow: '0 20px 60px rgba(0,0,0,0.45)' }} data-testid="ital-prevsl-video">
              <div className="aspect-video"><YT id={PRE_VSL_ID} title="HCF — Il sistema in 5 minuti" /></div>
            </div>
            {gSent ? (
              <div className="rounded-2xl border p-6 text-center" style={{ borderColor: `${GOLD}55`, background: `${INK}B8`, backdropFilter: 'blur(16px)' }} data-testid="ital-gate-unlocked">
                <CheckCircle2 className="w-10 h-10 mx-auto" style={{ color: GOLD }} />
                <div className="text-xl mt-3" style={{ color: IVORY }}>{brochureName ? `Brochure ${brochureName} in arrivo!` : 'Richiesta inviata!'}</div>
                <p className="text-sm font-sans mt-1" style={{ color: SAND }}>
                  {brochureName
                    ? `Ti abbiamo inviato la brochure ${brochureName} via email. Ora guarda i video qui sotto: prima capisci il sistema, poi prenota la consulenza strategica col CEO.`
                    : 'Riceverai il dossier Albania. Intanto prosegui nel percorso.'}
                </p>
                <a
                  href={calendly} target="_blank" rel="noopener noreferrer"
                  className="ital-gold-btn mt-5 w-full rounded-full min-h-12 py-2.5 px-4 text-[11px] sm:text-[12px] font-sans font-bold tracking-[0.12em] sm:tracking-[0.2em] uppercase text-center leading-snug inline-flex items-center justify-center gap-2"
                  data-testid="ital-gate-continue"
                >
                  <CalendarClock className="w-4 h-4" /> Prenota una consulenza strategica col CEO
                </a>
              </div>
            ) : (
              <form
                onSubmit={submitHero}
                className="rounded-2xl border p-4 sm:p-5 md:p-6 space-y-3 font-sans"
                style={{ borderColor: `${GOLD}55`, background: `${INK}B8`, backdropFilter: 'blur(16px)', boxShadow: '0 24px 70px rgba(0,0,0,0.45)' }}
              >
                <div className="flex items-center gap-2 text-[11px] font-bold tracking-[0.25em] uppercase" style={{ color: GOLD }}>
                  <FileCheck2 className="w-4 h-4" /> Ricevi il dossier Albania
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <LuxField icon={User} value={gName} onChange={(e) => setGName(e.target.value)} placeholder="Nome e cognome *" autoComplete="name" testId="ital-gate-name" />
                  <LuxField icon={Phone} type="tel" value={gPhone} onChange={(e) => setGPhone(e.target.value)} placeholder="Telefono (consigliato)" autoComplete="tel" testId="ital-gate-phone" />
                </div>
                <LuxField icon={Mail} type="email" value={gEmail} onChange={(e) => setGEmail(e.target.value)} placeholder="Email *" autoComplete="email" testId="ital-gate-email" />
                <button
                  type="submit"
                  disabled={gName.trim().length < 2 || !emailRe.test(gEmail.trim()) || gSending}
                  className="ital-gold-btn w-full rounded-full min-h-12 py-2.5 px-4 text-[11px] sm:text-[13px] font-bold tracking-[0.12em] sm:tracking-[0.2em] uppercase text-center leading-snug inline-flex items-center justify-center gap-2"
                  data-testid="ital-gate-submit"
                >
                  {gSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Inizia il percorso
                </button>
                <p className="text-[11px] text-center" style={{ color: SAND }}>
                  Inviando accetti la nostra{' '}
                  <Link to="/privacy" className="underline" style={{ color: IVORY }} data-testid="ital-gate-privacy">Privacy Policy</Link>.
                </p>
              </form>
            )}
          </div>
        </div>
      </Scene>

      {/* ════════ ★ Fase 96 (Doc 02) — DOPPIO INGRESSO + SCELTA GUIDATA (paese → obiettivo → landing dedicata) ════════ */}
      <HubGuidedChoice
        calendly={calendly}
        onTerritory={setTerritory}
        scrollTo={scrollTo}
        entry={guideEntry}
        onEntry={setGuideEntry}
        funnel="it"
        tokens={{ ink: INK, emerald: EMERALD, gold: GOLD, goldDeep: GOLD_DEEP, ivory: IVORY, sand: SAND }}
        id="percorso-guidato"
        testIdPrefix="ital-guide"
      />

      {/* ════════ 02/09 — NON È SOLO IMMOBILIARE + VSL REALE ════════
          Layout 2 colonne: copy a sinistra, VSL a destra (desktop bilanciato). */}
          <Scene n={2} id="ecosistema">
            <div className="my-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div className="max-w-2xl">
                <Kicker>Ecosistema HCF</Kicker>
                <h2 className="mt-5 text-4xl md:text-5xl xl:text-6xl leading-[1.05] tracking-tight" data-testid="ital-s2-title">
                  <span style={{ color: IVORY }}>Non è solo</span><br />
                  <span style={{ color: GOLD }}>immobiliare</span>
                </h2>
                <p className="mt-6 text-base md:text-lg leading-relaxed font-sans" style={{ color: IVORY }}>
                  Un asset da solo non basta.<br className="hidden md:block" />{' '}
                  HCF unisce real estate, lettura del territorio,<br className="hidden md:block" />{' '}
                  verifica sul campo e presenza reale per aiutarti<br className="hidden md:block" />{' '}
                  a costruire un patrimonio più consapevole.
                </p>
                <div className="mt-9 grid grid-cols-2 sm:grid-cols-4 gap-y-5">
                  {[{ icon: MapPin, label: 'Territorio' }, { icon: FileCheck2, label: 'Verifica' }, { icon: Landmark, label: 'Real Estate' }, { icon: Users, label: 'Experience' }].map((it, i) => (
                    <div key={it.label} className={`flex flex-col items-start gap-2 ${i > 0 ? 'sm:border-l sm:pl-6' : ''}`} style={{ borderColor: `${GOLD}44` }}>
                      <it.icon className="w-7 h-7" style={{ color: GOLD }} strokeWidth={1.4} />
                      <span className="text-sm md:text-base" style={{ color: IVORY }}>{it.label}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-8 text-base md:text-lg" style={{ color: SAND }}>
                  Prima capisci il sistema.<br />Poi scegli cosa approfondire.
                </p>
              </div>
              {/* Colonna destra — VSL REALE: promuove il Verification Pass */}
              <div className="w-full max-w-xl lg:ml-auto space-y-5">
                <div className="flex items-center gap-3 text-[11px] md:text-xs font-sans font-semibold tracking-[0.3em] uppercase" style={{ color: SAND }}>
                  <PlayCircle className="w-4 h-4" style={{ color: GOLD }} />
                  Il percorso raccontato da chi lo guida
                  <span className="inline-block flex-1 border-t" style={{ borderColor: `${GOLD}66` }} />
                </div>
                <div className="rounded-2xl overflow-hidden border" style={{ borderColor: `${GOLD}66`, boxShadow: '0 20px 60px rgba(0,0,0,0.45)' }} data-testid="ital-vsl-video">
                  <div className="aspect-video"><YT id={VSL_ID} title="HCF — VSL Verification Pass" /></div>
                </div>
                <IvoryPill onClick={() => scrollTo('verification')} testId="ital-vsl-cta" className="w-full">Scopri il Verification Pass</IvoryPill>
              </div>
            </div>
          </Scene>

          {/* ════════ 03/09 — COS'È UN'INFRASTRUTTURA PATRIMONIALE? ════════
              Layout 2 colonne: titolo+copy a sinistra, 4 pilastri in card a destra. */}
          <Scene n={3} id="infrastruttura">
            <div className="my-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div className="max-w-2xl">
                <h2 className="text-4xl md:text-5xl xl:text-6xl leading-[1.05] tracking-tight" data-testid="ital-s3-title">
                  <span style={{ color: IVORY }}>Cos&rsquo;è un&rsquo;</span><br />
                  <span style={{ color: GOLD }}>infrastruttura<br />patrimoniale?</span>
                </h2>
                <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed font-sans" style={{ color: IVORY }}>
                  L&rsquo;immobile è solo il primo livello.
                  Il vero valore nasce quando asset, cash flow,
                  struttura fiscale e continuità lavorano insieme.
                </p>
                <div className="mt-10">
                  <span className="text-2xl md:text-3xl" style={{ color: IVORY }}>Acquisisci. Produci.<br />Organizza. Trasmetti.</span>
                  <div className="mt-3 w-16 border-t-2" style={{ borderColor: GOLD }} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { n: '01', icon: Landmark, t: 'Asset', d: 'Selezione, territorio e due diligence' },
                  { n: '02', icon: BarChart3, t: 'Cash Flow', d: 'Hospitality, domanda reale e gestione' },
                  { n: '03', icon: Layers, t: 'Organizzazione fiscale', d: 'Struttura, compliance e protezione del netto' },
                  { n: '04', icon: Network, t: 'Eredità', d: 'Governance, trust, VCC e continuità' },
                ].map((p) => (
                  <div
                    key={p.n}
                    className="rounded-2xl border p-5 md:p-6 transition-transform duration-200 hover:-translate-y-0.5"
                    style={{ borderColor: `${GOLD}55`, background: `${EMERALD}B3`, backdropFilter: 'blur(12px)' }}
                    data-testid={`ital-pillar-${p.n}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="w-12 h-12 rounded-full border grid place-items-center" style={{ borderColor: GOLD }}>
                        <p.icon className="w-6 h-6" style={{ color: GOLD }} strokeWidth={1.4} />
                      </span>
                      <span className="text-xl" style={{ color: `${GOLD}99` }}>{p.n}</span>
                    </div>
                    <div className="mt-4 text-base md:text-lg font-semibold uppercase tracking-wide leading-snug" style={{ color: IVORY }}>{p.t}</div>
                    <p className="mt-1.5 text-sm font-sans leading-relaxed" style={{ color: SAND }}>{p.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </Scene>

          {/* ════════ 04/09 — STEP 1 · TERRITORIO + VERIFICATION PASS ════════ */}
          <Scene n={4} id="verification">
            <div className="my-auto w-full">
              <Kicker>Step 1</Kicker>
              <h2 className="mt-5 text-4xl md:text-5xl xl:text-6xl leading-[1.05] tracking-tight" data-testid="ital-s4-title">
                <span style={{ color: IVORY }}>Scegli il territorio</span><br />
                <span style={{ color: GOLD }}>da verificare</span>
              </h2>
              <p className="mt-5 max-w-xl text-base md:text-lg leading-relaxed font-sans" style={{ color: IVORY }}>
                Ogni campagna deve aprire un solo contesto:<br className="hidden md:block" />{' '}
                un paese, una logica, una prossima decisione.
              </p>

              <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                {/* Territori — ★ Fase 52: Albania e Indonesia selezionabili, Thailandia sold out */}
                <div className="space-y-3" data-testid="ital-territories">
                  {[
                    { key: 'AL', tid: 'albania', label: 'Albania', status: 'available', img: '/funnels/indonesia/green-paradise-lombok/photos/gp-07-albania-card.webp' },
                    { key: 'ID', tid: 'indonesia', label: 'Indonesia', status: 'available', img: '/funnels/indonesia/bali-villas/bali-hero.webp' },
                    { key: 'TH', tid: 'thailandia', label: 'Thailandia', status: 'soldout', selectable: true, note: '€3.500', img: '/funnels/thailand/lux-harmony/lh-hero-sunset.webp' },
                    { key: 'ZB', tid: 'zanzibar', label: 'Zanzibar', status: 'soldout', iconFallback: Palmtree },
                    { key: 'ES', tid: 'spagna', label: 'Spagna', status: 'closed', iconFallback: Building2 },
                  ].map((tr) => {
                    const st = { available: { label: 'Available', dot: '#2FBF71' }, soldout: { label: 'Sold Out', dot: '#E5484D' }, closed: { label: 'Closed', dot: '#8A8577' } }[tr.status];
                    const selectable = tr.status === 'available' || tr.selectable === true;
                    const isSel = selectable && territory === tr.key;
                    return (
                      <button
                        key={tr.key} type="button"
                        onClick={() => { if (!selectable) return; setTerritory(tr.key); scrollTo('vp-card'); }}
                        className="w-full flex items-center gap-4 rounded-xl border p-2.5 pr-4 text-left transition-transform duration-150 hover:scale-[1.01]"
                        style={{
                          borderColor: isSel ? GOLD : `${GOLD}33`,
                          background: isSel ? `${EMERALD}F0` : `${EMERALD}80`,
                          boxShadow: isSel ? `0 0 0 1px ${GOLD}, 0 10px 30px rgba(0,0,0,0.35)` : 'none',
                          opacity: tr.status === 'available' ? 1 : 0.68,
                          cursor: selectable ? 'pointer' : 'default',
                        }}
                        data-testid={`ital-territory-${tr.tid}`}
                      >
                        <span className="w-20 h-14 rounded-lg overflow-hidden shrink-0 grid place-items-center" style={{ background: INK }}>
                          {tr.img ? <img src={tr.img} alt={tr.label} className="w-full h-full object-cover" loading="lazy" /> : <tr.iconFallback className="w-6 h-6" style={{ color: `${GOLD}99` }} strokeWidth={1.4} />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-lg tracking-wide uppercase" style={{ color: IVORY }}>{tr.label}</span>
                          <span className="flex items-center gap-1.5 text-xs font-sans" style={{ color: SAND }}>
                            <span className="w-2 h-2 rounded-full inline-block" style={{ background: st.dot }} /> {st.label}{tr.note ? ` · ${tr.note}` : ''}
                          </span>
                        </span>
                        {isSel && (
                          <span className="shrink-0 w-9 h-9 rounded-full grid place-items-center" style={{ background: IVORY }}>
                            <ArrowRight className="w-4 h-4" style={{ color: '#143C2B' }} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                  {/* ★ Fase 59 — video DINAMICO per territorio sotto la lista paesi */}
                  <VpVideoPlaylist
                    videos={vpVideosFor(territory)}
                    lang="it"
                    territory={territory}
                    funnel="it"
                    tokens={{ ink: INK, emerald: EMERALD, gold: GOLD, ivory: IVORY, sand: SAND }}
                    testIdPrefix="ital-territory-video"
                    className="!mt-5"
                  />
                </div>

                {/* Card VP + Extra Pass */}
                <div className="space-y-4">
                  <div
                    id="vp-card"
                    className="rounded-2xl border p-5 sm:p-7 md:p-8"
                    style={{ borderColor: `${GOLD}77`, background: `linear-gradient(160deg, ${EMERALD}F5, ${INK}EE)`, backdropFilter: 'blur(16px)', boxShadow: '0 24px 70px rgba(0,0,0,0.5)' }}
                    data-testid="ital-vp-card"
                  >
                    <div className="text-2xl md:text-3xl" style={{ color: IVORY }}>Verification Pass</div>
                    <div className="text-4xl md:text-5xl mt-1" style={{ color: GOLD }} data-testid="ital-vp-country">{vpCountry.name}</div>
                    <p className="mt-4 text-sm md:text-base font-sans leading-relaxed" style={{ color: IVORY }}>
                      {vpCountry.desc}
                    </p>
                    <div className="mt-6 text-[11px] font-sans font-bold tracking-[0.35em] uppercase" style={{ color: SAND }}>{vpCountry.rateLabel}</div>
                    <div className="flex items-end gap-3">
                      <span className="text-5xl sm:text-6xl md:text-7xl leading-none" style={{ color: GOLD }} data-testid="ital-vp-price">{vpCountry.price}</span>
                      <span className="pb-2 text-base font-sans" style={{ color: SAND }}>a persona</span>
                    </div>
                    {/* ★ Fase 47/54 — countdown oppure banner lista d'attesa (paese sold out) */}
                    {vpCountry.soldOut ? (
                      <div className="mt-6 rounded-lg border px-4 py-3.5 flex items-start gap-3" style={{ borderColor: '#E5484D66', background: `${INK}B3` }} data-testid="ital-vp-waitlist">
                        <Star className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#E5484D', fill: '#E5484D' }} />
                        <p className="text-sm font-sans leading-relaxed" style={{ color: IVORY }}>
                          <b style={{ color: '#E5484D' }}>Tutte le date sono sold out.</b><br />
                          Entra in lista d&rsquo;attesa: ti avvisiamo appena si libera
                          un posto o apriamo nuove partenze.
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="mt-6 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[10px] sm:text-[11px] font-sans font-bold tracking-[0.14em] sm:tracking-[0.2em] uppercase" style={{ color: SAND }}>
                          <span>{vp.running ? 'Verification Pass in corso' : 'La prossima data parte tra'}</span>
                          <span style={{ color: GOLD }} data-testid="ital-vp-next-date">{vp.date.label}</span>
                        </div>
                        <div className="mt-2.5 grid grid-cols-4 gap-2.5" data-testid="ital-vp-countdown">
                          {[{ v: vp.d, l: 'Giorni' }, { v: vp.h, l: 'Ore' }, { v: vp.m, l: 'Minuti' }, { v: vp.s, l: 'Secondi' }].map((b) => (
                            <div key={b.l} className="rounded-lg border py-3 text-center" style={{ borderColor: `${GOLD}55`, background: `${INK}80` }}>
                              <div className="text-xl sm:text-2xl md:text-3xl" style={{ color: IVORY }}>{String(b.v).padStart(2, '0')}</div>
                              <div className="text-[9px] font-sans font-bold tracking-[0.25em] uppercase mt-0.5" style={{ color: SAND }}>{b.l}</div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    {/* ★ Fase 47/52 — date ufficiali con posti rimanenti (per paese) */}
                    <div className="mt-5 space-y-2" data-testid="ital-vp-dates">
                      {vpActiveDates.map((d) => {
                        const isNext = d.id === vp.date.id;
                        const pct = Math.round((d.left / d.total) * 100);
                        const soldOut = (d.left ?? 0) <= 0;
                        return (
                          <div
                            key={d.id}
                            className="rounded-lg border px-3.5 py-2.5"
                            style={{
                              borderColor: isNext ? GOLD : `${GOLD}33`,
                              background: isNext ? `${INK}B3` : `${INK}66`,
                              boxShadow: isNext ? `0 0 0 1px ${GOLD}` : 'none',
                              opacity: soldOut ? 0.75 : 1,
                            }}
                            data-testid={`ital-vp-date-${d.id}`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-sm font-sans font-semibold" style={{ color: IVORY }}>{d.label}</span>
                              <span className="text-[11px] font-sans font-bold tracking-wide uppercase whitespace-nowrap" style={{ color: d.left <= 5 ? '#E5484D' : GOLD }}>
                                {soldOut ? 'Sold out' : `${d.left} posti su ${d.total}`}
                              </span>
                            </div>
                            <div className="mt-1.5 h-1 rounded-full overflow-hidden" style={{ background: `${GOLD}22` }}>
                              <div className="h-full rounded-full" style={{ width: `${soldOut ? 100 : pct}%`, background: d.left <= 5 ? '#E5484D' : `linear-gradient(90deg, ${GOLD_DEEP}, ${GOLD})` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <button
                      type="button" onClick={() => scrollTo('finale')}
                      className="mt-7 w-full rounded-full min-h-12 py-2.5 px-4 text-[11px] sm:text-[13px] font-sans font-bold tracking-[0.12em] sm:tracking-[0.2em] uppercase text-center leading-snug inline-flex items-center justify-center gap-2 transition-transform duration-200 hover:scale-[1.02]"
                      style={{ background: IVORY, color: '#143C2B', boxShadow: `0 0 0 1px ${GOLD}` }}
                      data-testid="ital-vp-cta"
                    >
                      {vpCountry.soldOut ? "Entra in lista d'attesa" : 'Verifica il paese'} <ArrowRight className="w-4 h-4" />
                    </button>
                    {vpCountry.soldOut ? (
                      <a
                        href={calendly} target="_blank" rel="noopener noreferrer"
                        className="mt-3 w-full rounded-full min-h-11 py-2 px-4 text-[11px] sm:text-[12px] font-sans font-bold tracking-[0.12em] sm:tracking-[0.2em] uppercase text-center leading-snug inline-flex items-center justify-center gap-2 border transition-colors duration-150 hover:bg-white/5"
                        style={{ borderColor: GOLD, color: IVORY, background: 'transparent' }}
                        data-testid="ital-vp-cta-2"
                      >
                        Consulenza strategica col CEO <ArrowRight className="w-4 h-4" />
                      </a>
                    ) : (
                      <button
                        type="button" onClick={() => scrollTo('finale')}
                        className="mt-3 w-full rounded-full min-h-11 py-2 px-4 text-[11px] sm:text-[12px] font-sans font-bold tracking-[0.12em] sm:tracking-[0.2em] uppercase text-center leading-snug inline-flex items-center justify-center gap-2 border transition-colors duration-150 hover:bg-white/5"
                        style={{ borderColor: GOLD, color: IVORY, background: 'transparent' }}
                        data-testid="ital-vp-cta-2"
                      >
                        {vpCountry.lockCta} <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* ★ Fase 47 — EXTRA PASS PRIVATE (solo Albania · infrasettimanale · €500 · max 5) */}
                  {territory === 'AL' && (
                  <div
                    className="rounded-2xl border p-5 sm:p-6 md:p-7"
                    style={{ borderColor: `${GOLD}99`, background: `linear-gradient(160deg, ${INK}F8, #120D04EE)`, backdropFilter: 'blur(16px)', boxShadow: '0 24px 70px rgba(0,0,0,0.5)' }}
                    data-testid="ital-extra-pass-card"
                  >
                    <div className="flex items-center gap-2 text-[11px] font-sans font-bold tracking-[0.3em] uppercase" style={{ color: GOLD }}>
                      <Star className="w-4 h-4" style={{ fill: GOLD }} /> Extra Pass Private
                    </div>
                    <div className="mt-2 flex items-end gap-3">
                      <span className="text-4xl md:text-5xl leading-none" style={{ color: IVORY }} data-testid="ital-extra-pass-price">€500</span>
                      <span className="pb-1 text-sm font-sans" style={{ color: SAND }}>infrasettimanale · gruppi ristretti</span>
                    </div>
                    <p className="mt-3 text-sm font-sans leading-relaxed" style={{ color: IVORY }}>
                      Vista la grande richiesta dei weekend, abbiamo aperto anche
                      <b> date infrasettimanali</b> in formato privato:
                      massimo <b>5 investitori</b> per data, agenda dedicata.
                    </p>
                    <div className="mt-4 grid grid-cols-2 gap-2" data-testid="ital-extra-pass-dates">
                      {extraDates.map((d) => (
                        <div key={d.id} className="rounded-lg border px-3 py-2.5" style={{ borderColor: `${GOLD}44`, background: `${INK}80` }} data-testid={`ital-extra-date-${d.id}`}>
                          <div className="text-sm font-sans font-semibold" style={{ color: IVORY }}>{d.label}</div>
                          <div className="text-[11px] font-sans font-bold tracking-wide uppercase mt-0.5" style={{ color: GOLD }}>max {d.max} investitori</div>
                        </div>
                      ))}
                    </div>
                    <a
                      href={calendly} target="_blank" rel="noopener noreferrer"
                      className="ital-gold-btn mt-5 w-full rounded-full min-h-11 py-2 px-4 text-[11px] sm:text-[12px] font-sans font-bold tracking-[0.12em] sm:tracking-[0.2em] uppercase text-center leading-snug inline-flex items-center justify-center gap-2"
                      data-testid="ital-extra-pass-cta"
                    >
                      Richiedi il tuo posto <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                  )}
                </div>
              </div>
            </div>
          </Scene>

          {/* ════════ 05/09 — STEP 2 · SCEGLI L'ASSET ════════ */}
          <Scene n={5} id="asset">
            <div className="my-auto w-full">
              <Kicker>Step 2</Kicker>
              <h2 className="mt-5 text-4xl md:text-5xl xl:text-6xl leading-[1.05] tracking-tight" data-testid="ital-s5-title">
                <span style={{ color: IVORY }}>Scegli l&rsquo;asset</span><br />
                <span style={{ color: GOLD }}>da approfondire</span>
              </h2>
              <p className="mt-5 max-w-xl text-base md:text-lg leading-relaxed font-sans" style={{ color: IVORY }}>
                Dopo il territorio arriva l&rsquo;asset.<br className="hidden md:block" />{' '}
                Non un catalogo infinito, ma una selezione<br className="hidden md:block" />{' '}
                coerente con il percorso.
              </p>
              {/* ★ Fase 54 — indicatore del territorio selezionato */}
              <div className="mt-5 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-[11px] font-sans font-bold tracking-[0.22em] uppercase" style={{ borderColor: `${GOLD}77`, background: `${INK}B3`, color: GOLD }} data-testid="ital-assets-territory-badge">
                <MapPin className="w-3.5 h-3.5" /> Territorio selezionato: {vpCountry.name}
              </div>
              {territory === 'ID' ? (
              /* ★ Fase 55 — Indonesia: card prodotto compatte allineate a
                 sinistra. "Scopri di più" apre la mini-landing dedicata
                 (pagina a sé, usabile anche nelle campagne dirette). */
              <IndonesiaAssetCards
                cards={INDONESIA_CARDS}
                tokens={{ gold: GOLD, ivory: IVORY, sand: SAND, emerald: EMERALD }}
                ctaLabel="Scopri di più"
                testIdPrefix="ital-id"
                funnel="it"
              />
              ) : sceneAssets.length > 0 ? (
              <div className={`mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5 ${sceneAssets.length > 2 ? 'lg:grid-cols-3' : 'max-w-3xl'}`} data-testid="ital-assets-grid">
                {sceneAssets.map((p) => (
                  <div key={p.slug} className="group rounded-2xl border overflow-hidden transition-transform duration-200 hover:-translate-y-0.5" style={{ borderColor: `${GOLD}55`, background: `${EMERALD}CC` }} data-testid={`ital-asset-${p.slug}`}>
                    <div className="h-44 overflow-hidden">
                      <img src={p.image_url || p.hero_image} alt={p.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.04]" loading="lazy" />
                    </div>
                    <div className="p-5">
                      <div className="text-xl uppercase tracking-wide" style={{ color: IVORY }}>{p.short_name || p.name}</div>
                      <p className="mt-1.5 text-sm font-sans leading-snug" style={{ color: SAND }}>{assetCopy[p.slug] || p.tagline}</p>
                      <Link to={assetHref(p.slug)} data-testid={`ital-asset-cta-${p.slug}`}>
                        <span className="mt-4 w-full rounded-full h-10 text-[11px] font-sans font-bold tracking-[0.22em] uppercase inline-flex items-center justify-center gap-2 transition-transform duration-150 hover:scale-[1.02]" style={{ background: IVORY, color: '#143C2B', boxShadow: `0 0 0 1px ${GOLD}` }}>
                          Approfondisci <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
              ) : (
              /* ★ Fase 54 — territorio sold out / selezione esaurita → lista d'attesa */
              <div className="mt-8 rounded-2xl border p-5 sm:p-7 md:p-8 max-w-2xl" style={{ borderColor: `${GOLD}55`, background: `${EMERALD}B3`, backdropFilter: 'blur(12px)' }} data-testid="ital-assets-empty">
                <div className="text-2xl md:text-3xl leading-snug" style={{ color: IVORY }}>
                  Selezione {vpCountry.name}<br /><span style={{ color: GOLD }}>momentaneamente esaurita</span>
                </div>
                <p className="mt-3 text-sm md:text-base font-sans leading-relaxed" style={{ color: SAND }}>
                  Gli asset di questo territorio sono andati sold out.
                  Entra in lista d&rsquo;attesa: ti avvisiamo appena apriamo la prossima selezione.
                </p>
                <button
                  type="button" onClick={() => scrollTo('finale')}
                  className="ital-gold-btn mt-6 rounded-full h-11 px-7 text-[12px] font-sans font-bold tracking-[0.2em] uppercase inline-flex items-center justify-center gap-2"
                  data-testid="ital-assets-empty-cta"
                >
                  Entra in lista d&rsquo;attesa <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              )}
              <p className="mt-10 text-xl md:text-2xl leading-snug" style={{ color: IVORY }}>
                Non devi scegliere oggi quello giusto.<br className="hidden md:block" />{' '}
                Devi capire quale merita attenzione.
              </p>
              <div className="mt-3 w-12 border-t-2" style={{ borderColor: GOLD }} />
            </div>
          </Scene>

          {/* ════════ 06/09 — IL RENDIMENTO NON NASCE NEL RENDERING ════════ */}
          <Scene n={6} id="rendimento">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src="/funnels/hcf-logo.png" alt="HCF Property" className="h-8 w-auto" />
                <span className="h-6 w-px" style={{ background: `${GOLD}55` }} />
                {/* ★ Fase 49 — logo ufficiale HCF Travel */}
                <img src="/funnels/hcf-travel-logo.png" alt="HCF Travel" className="h-8 w-auto" data-testid="ital-travel-logo" />
              </div>
            </div>
            <div className="my-auto w-full grid grid-cols-1 lg:grid-cols-[1fr_minmax(0,420px)] gap-10 lg:gap-14 items-center">
              <div className="max-w-2xl">
              <h2 className="text-4xl md:text-5xl xl:text-6xl leading-[1.05] tracking-tight" data-testid="ital-s6-title">
                <span style={{ color: IVORY }}>Il rendimento</span><br />
                <span style={{ color: GOLD }}>non nasce<br />nel rendering</span>
              </h2>
              <p className="mt-4 text-2xl md:text-3xl" style={{ color: IVORY }}>Nasce dalla domanda reale.</p>
              <p className="mt-5 max-w-xl text-base md:text-lg leading-relaxed font-sans" style={{ color: IVORY }}>
                Per questo la lettura del territorio non parte
                solo dal real estate. Il tour operator aggiunge
                una lente concreta su flussi, occupazione,
                permanenza e comportamento del viaggiatore.
              </p>
              <div className="mt-6 w-12 border-t-2" style={{ borderColor: GOLD }} />
              <div className="mt-7">
                <a
                  href="/funnels/certificato-tour-operator.pdf" target="_blank" rel="noopener noreferrer"
                  className="inline-flex w-full sm:w-auto items-center justify-center text-center gap-2 rounded-2xl sm:rounded-full px-5 sm:px-7 py-3.5 text-[11px] sm:text-[12px] font-sans font-bold tracking-[0.14em] sm:tracking-[0.22em] uppercase transition-transform duration-150 hover:scale-[1.02]"
                  style={{ background: IVORY, color: '#143C2B', boxShadow: `0 0 0 1px ${GOLD}` }}
                  data-testid="ital-tour-operator-badge"
                >
                  <FileCheck2 className="w-4 h-4" /> Tour Operator Registrato · Vedi il certificato
                </a>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-2 max-w-lg">
                {[{ icon: Users, label: 'Flussi turistici' }, { icon: BedDouble, label: 'Occupazione' }, { icon: CalendarClock, label: 'Permanenza media' }].map((it, i) => (
                  <div key={it.label} className={`flex flex-col items-start gap-2 ${i > 0 ? 'border-l pl-4' : ''}`} style={{ borderColor: `${GOLD}44` }}>
                    <it.icon className="w-7 h-7" style={{ color: GOLD }} strokeWidth={1.4} />
                    <span className="text-sm md:text-base leading-tight" style={{ color: IVORY }}>{it.label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-12">
                <div className="text-xl md:text-2xl" style={{ color: GOLD }}>Dati reali &gt; ipotesi</div>
                <p className="text-sm md:text-base font-sans mt-1" style={{ color: SAND }}>Prima del progetto viene il territorio.</p>
              </div>
              </div>

              {/* ★ Fase 49 — Certificato ufficiale Tour Operator (HCF Travel) */}
              <div
                className="w-full rounded-2xl border p-5 font-sans"
                style={{ borderColor: `${GOLD}66`, background: `${INK}C4`, backdropFilter: 'blur(14px)', boxShadow: '0 24px 70px rgba(0,0,0,0.45)' }}
                data-testid="ital-certificate-card"
              >
                <div className="flex items-center justify-between gap-3">
                  <img src="/funnels/hcf-travel-logo.png" alt="HCF Travel" className="h-7 w-auto" />
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: GOLD }}>
                    <ShieldCheck className="w-4 h-4" /> Certificato ufficiale
                  </span>
                </div>
                <a href="/funnels/certificato-tour-operator.pdf" target="_blank" rel="noopener noreferrer" className="block mt-4 group" data-testid="ital-certificate-preview">
                  <div className="rounded-xl overflow-hidden border transition-transform duration-200 group-hover:scale-[1.015]" style={{ borderColor: `${GOLD}55` }}>
                    <img
                      src="/funnels/certificato-tour-operator.jpg"
                      alt="Certificato di registrazione Tour Operator — HCF Travel"
                      className="w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                </a>
                <p className="mt-3 text-[12px] leading-relaxed" style={{ color: SAND }}>
                  Registrazione Tour Operator <b style={{ color: IVORY }}>№ RK-01-8681</b> —
                  Ministero del Turismo, rilasciata a <b style={{ color: IVORY }}>HCF Travel</b>.
                </p>
                <a
                  href="/funnels/certificato-tour-operator.pdf" target="_blank" rel="noopener noreferrer"
                  className="mt-3 w-full rounded-full h-10 text-[11px] font-bold tracking-[0.2em] uppercase inline-flex items-center justify-center gap-2 border transition-colors duration-150 hover:bg-white/5"
                  style={{ borderColor: GOLD, color: IVORY }}
                  data-testid="ital-certificate-open"
                >
                  Apri il certificato (PDF) <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </Scene>

          {/* ════════ 07/09 — PROVE, NON PROMESSE (testimonianze) ════════
              Layout 2 colonne: copy+Trustpilot a sinistra, griglia video 2×2 a destra. */}
          <Scene n={7} id="prove">
            <div className="my-auto w-full grid grid-cols-1 lg:grid-cols-[minmax(0,440px)_1fr] gap-10 lg:gap-14 items-center">
              <div>
                <Kicker>Prove</Kicker>
                <h2 className="mt-5 text-4xl md:text-5xl xl:text-6xl leading-[1.05] tracking-tight" data-testid="ital-s7-title">
                  <span style={{ color: IVORY }}>Prove,</span><br />
                  <span style={{ color: GOLD }}>non promesse</span>
                </h2>
                <p className="mt-5 text-base md:text-lg leading-relaxed font-sans" style={{ color: IVORY }}>
                  Clienti reali, experience reali e<br className="hidden md:block" />{' '}
                  domande scomode affrontate apertamente.
                </p>
                {/* ★ Riprova sociale Trustpilot (richiesta cliente) */}
                <a
                  href="https://www.trustpilot.com/review/housecashflows.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl sm:rounded-full border px-5 sm:px-6 py-3 transition-transform duration-150 hover:scale-[1.02]"
                  style={{ borderColor: `${GOLD}66`, background: `${EMERALD}B3` }}
                  data-testid="ital-trustpilot-link"
                >
                  <span className="w-7 h-7 rounded-md grid place-items-center shrink-0" style={{ background: '#00B67A' }}>
                    <Star className="w-4 h-4" style={{ color: '#fff', fill: '#fff' }} />
                  </span>
                  <span className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4" style={{ color: '#00B67A', fill: '#00B67A' }} />)}
                  </span>
                  <span className="hidden sm:block h-5 w-px" style={{ background: `${GOLD}55` }} />
                  <span className="text-sm font-sans" style={{ color: IVORY }}>
                    Recensioni verificabili su <b>Trustpilot</b>
                  </span>
                  <ArrowRight className="w-4 h-4" style={{ color: GOLD }} />
                </a>
                <p className="mt-10 text-xl md:text-2xl leading-snug" style={{ color: IVORY }}>
                  La credibilità si costruisce<br className="hidden md:block" />{' '}
                  mostrando il percorso, non solo raccontandolo.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="ital-testimonials-grid">
                {TESTIMONIALS_FEATURED.map((v) => <TestimonialCard key={v.id} id={v.id} label={v.label} name={v.name} />)}
              </div>
            </div>
            {/* ★ 2026-10-19 — altre testimonianze (9 nuove + recensione completa), espandibili */}
            {TESTIMONIALS_MORE.length > 0 && (
              <div className="mt-8 w-full">
                {showAllTestimonials ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="ital-testimonials-more-grid">
                    {TESTIMONIALS_MORE.map((v) => <TestimonialCard key={v.id} id={v.id} label={v.label} name={v.name} />)}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowAllTestimonials(true)}
                    className="mx-auto flex items-center gap-3 rounded-full border px-6 py-3 text-sm font-sans transition-transform duration-150 hover:scale-[1.02]"
                    style={{ borderColor: `${GOLD}99`, background: `${EMERALD}B3`, color: IVORY }}
                    data-testid="ital-testimonials-show-more"
                  >
                    <PlayCircle className="w-5 h-5" style={{ color: GOLD }} strokeWidth={1.4} />
                    Guarda altre {TESTIMONIALS_MORE.length} testimonianze
                    <ArrowRight className="w-4 h-4" style={{ color: GOLD }} />
                  </button>
                )}
              </div>
            )}
          </Scene>

          {/* ════════ 08/09 — STEP 3 · COMPLETA LA TUA STRUTTURA ════════ */}
          <Scene n={8} id="struttura">
            <div className="my-auto w-full">
              <Kicker>Step 3</Kicker>
              <h2 className="mt-5 text-4xl md:text-5xl xl:text-6xl leading-[1.05] tracking-tight" data-testid="ital-s8-title">
                <span style={{ color: IVORY }}>Completa</span><br />
                <span style={{ color: GOLD }}>la tua struttura</span>
              </h2>
              <p className="mt-5 max-w-xl text-base md:text-lg leading-relaxed font-sans" style={{ color: IVORY }}>
                Da qui in avanti non serve cliccare di più.<br className="hidden md:block" />{' '}
                Serve capire il tuo caso con un Senior Associate.
              </p>
              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {/* Path checklist */}
                <div className="rounded-2xl border p-5 sm:p-6" style={{ borderColor: `${GOLD}55`, background: `${EMERALD}B3`, backdropFilter: 'blur(12px)' }} data-testid="ital-path-card">
                  <div className="text-[11px] font-sans font-bold tracking-[0.3em] uppercase" style={{ color: IVORY }}>Your HCF Path</div>
                  <div className="mt-4 divide-y" style={{ borderColor: `${GOLD}33` }}>
                    {[
                      { icon: MapPin, k: 'Territorio', v: 'Albania', done: true },
                      { icon: FileCheck2, k: 'Verification', v: 'Pass', done: true },
                      { icon: Landmark, k: 'Asset', v: 'Narea · Three Garden', done: true },
                      { icon: BarChart3, k: 'Cash Flow', v: 'Da configurare', done: false },
                      { icon: Layers, k: 'Fiscale', v: 'Da configurare', done: false },
                      { icon: Users, k: 'Patrimonio', v: 'Da configurare', done: false },
                    ].map((r) => (
                      <div key={r.k} className="flex items-center gap-3 py-3" style={{ borderColor: `${GOLD}33` }}>
                        <r.icon className="w-5 h-5 shrink-0" style={{ color: GOLD }} strokeWidth={1.5} />
                        <span className="w-24 text-sm" style={{ color: IVORY }}>{r.k}</span>
                        <span className="flex-1 text-sm font-sans" style={{ color: r.done ? IVORY : SAND }}>{r.v}</span>
                        {r.done
                          ? <CheckCircle2 className="w-5 h-5 shrink-0" style={{ color: GOLD }} />
                          : <Circle className="w-5 h-5 shrink-0" style={{ color: `${SAND}88` }} strokeWidth={1} strokeDasharray="3 3" />}
                      </div>
                    ))}
                  </div>
                </div>
                {/* Sessione Senior */}
                <div className="rounded-2xl border p-5 sm:p-6 flex flex-col" style={{ borderColor: `${GOLD}55`, background: `${EMERALD}B3`, backdropFilter: 'blur(12px)' }} data-testid="ital-session-card">
                  <span className="w-14 h-14 rounded-full border grid place-items-center" style={{ borderColor: GOLD }}>
                    <Users className="w-7 h-7" style={{ color: GOLD }} strokeWidth={1.3} />
                  </span>
                  <div className="mt-4 text-2xl leading-snug" style={{ color: IVORY }}>Sessione con<br />Senior Associate</div>
                  <div className="mt-2 w-10 border-t" style={{ borderColor: GOLD }} />
                  <ul className="mt-4 space-y-2.5 text-sm font-sans" style={{ color: IVORY }}>
                    {['obiettivo', 'utilizzo', 'budget', 'documenti', 'struttura'].map((x) => (
                      <li key={x} className="flex items-center gap-2.5">
                        <span className="w-2 h-2 rounded-full border" style={{ borderColor: GOLD }} /> {x}
                      </li>
                    ))}
                  </ul>
                  <a href={calendly} target="_blank" rel="noopener noreferrer" className="mt-auto pt-6" data-testid="ital-book-session">
                    <span className="w-full rounded-full min-h-11 py-2 px-4 text-[11px] sm:text-[12px] font-sans font-bold tracking-[0.12em] sm:tracking-[0.2em] uppercase text-center leading-snug inline-flex items-center justify-center gap-2 transition-transform duration-150 hover:scale-[1.02]" style={{ background: IVORY, color: '#143C2B', boxShadow: `0 0 0 1px ${GOLD}` }}>
                      Prenota la consulenza strategica <ArrowRight className="w-4 h-4" />
                    </span>
                  </a>
                </div>
                {/* Verifica, non promesse — terza colonna per equilibrio desktop */}
                <div className="rounded-2xl border p-5 sm:p-6 flex flex-col md:col-span-2 lg:col-span-1" style={{ borderColor: `${GOLD}55`, background: `${EMERALD}B3`, backdropFilter: 'blur(12px)' }} data-testid="ital-risk-card">
                  <span className="w-14 h-14 rounded-full border grid place-items-center" style={{ borderColor: GOLD }}>
                    <ShieldCheck className="w-7 h-7" style={{ color: GOLD }} strokeWidth={1.3} />
                  </span>
                  <div className="mt-4 text-2xl leading-snug" style={{ color: IVORY }}>Il rischio zero<br />non esiste</div>
                  <div className="mt-2 w-10 border-t" style={{ borderColor: GOLD }} />
                  <p className="mt-4 text-sm font-sans leading-relaxed" style={{ color: IVORY }}>
                    Lo rendiamo più verificabile: territorio, asset
                    e dati si controllano di persona, prima di decidere.
                  </p>
                  <ul className="mt-4 space-y-2.5 text-sm font-sans" style={{ color: IVORY }}>
                    {['verifica sul campo', 'dati del territorio', 'nessun obbligo di acquisto'].map((x) => (
                      <li key={x} className="flex items-center gap-2.5">
                        <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: GOLD }} /> {x}
                      </li>
                    ))}
                  </ul>
                  <a href={calendly} target="_blank" rel="noopener noreferrer" className="mt-auto pt-6" data-testid="ital-risk-cta">
                    <span className="w-full rounded-full min-h-11 py-2 px-4 text-[11px] sm:text-[12px] font-sans font-bold tracking-[0.12em] sm:tracking-[0.2em] uppercase text-center leading-snug inline-flex items-center justify-center gap-2 border transition-colors duration-150 hover:bg-white/5" style={{ borderColor: GOLD, color: IVORY }}>
                      Consulenza strategica col CEO <ArrowRight className="w-4 h-4" />
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </Scene>

          {/* ════════ 09/09 — FINAL CTA · INIZIA LA VERIFICA ════════
              Layout 2 colonne speculare all'hero: copy a sinistra, form a destra. */}
          <Scene n={9} id="finale">
            <div className="my-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div className="max-w-2xl">
                <Kicker>Final CTA</Kicker>
                <h2 className="mt-5 text-[2.6rem] sm:text-5xl md:text-6xl xl:text-7xl leading-[1.02] tracking-tight" data-testid="ital-s9-title">
                  <span style={{ color: IVORY }}>Inizia la</span><br />
                  <span style={{ color: GOLD }}>verifica</span>
                </h2>
                <p className="mt-6 text-lg md:text-xl leading-relaxed font-sans" style={{ color: IVORY }}>
                  Prima capisci il sistema.<br />Poi verifichi il territorio.<br />Poi scegli l&rsquo;asset.
                </p>
                <div className="mt-6 w-12 border-t-2" style={{ borderColor: GOLD }} />
                <p className="mt-5 text-base md:text-lg leading-relaxed" style={{ color: SAND }}>
                  HCF non ti chiede di comprare oggi.<br className="hidden md:block" />{' '}
                  Ti aiuta a capire cosa vale la pena approfondire.
                </p>
                <div className="mt-10 flex flex-wrap items-center gap-x-0 gap-y-3">
                  {[{ icon: Landmark, label: 'Asset' }, { icon: BarChart3, label: 'Cash Flow' }, { icon: Layers, label: 'Continuità' }].map((it, i) => (
                    <React.Fragment key={it.label}>
                      {i > 0 && <span className="hidden sm:block h-8 w-px mx-5" style={{ background: `${GOLD}55` }} />}
                      <span className="inline-flex items-center gap-2.5 mr-5 sm:mr-0">
                        <it.icon className="w-5 h-5" style={{ color: GOLD }} strokeWidth={1.6} />
                        <span className="text-sm md:text-base" style={{ color: IVORY }}>{it.label}</span>
                      </span>
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div className="w-full max-w-xl lg:ml-auto">
                {fSent ? (
                  <div className="rounded-2xl border p-6 sm:p-8 text-center" style={{ borderColor: `${GOLD}55`, background: `${INK}B3`, backdropFilter: 'blur(14px)' }} data-testid="ital-final-success">
                    <CheckCircle2 className="w-12 h-12 mx-auto" style={{ color: GOLD }} />
                    <div className="text-2xl mt-4" style={{ color: IVORY }}>Richiesta inviata!</div>
                    <p className="text-sm font-sans mt-1" style={{ color: SAND }}>Riceverai il dossier e un Senior Associate ti contatterà a breve.</p>
                  </div>
                ) : (
                  <form
                    onSubmit={submitFinal}
                    className="rounded-2xl border p-5 sm:p-6 md:p-7 space-y-3.5 font-sans"
                    style={{ borderColor: `${GOLD}55`, background: `${INK}B8`, backdropFilter: 'blur(16px)', boxShadow: '0 24px 70px rgba(0,0,0,0.45)' }}
                    data-testid="ital-final-form"
                  >
                    <div className="flex items-center gap-2 text-[11px] font-bold tracking-[0.25em] uppercase" style={{ color: GOLD }}>
                      <FileCheck2 className="w-4 h-4" /> {territory === 'TH' ? "Lista d'attesa Thailandia" : `Ricevi il dossier ${vpCountry.name}`}
                    </div>
                    <LuxField icon={User} placeholder="Nome e cognome *" value={fName} onChange={(e) => setFName(e.target.value)} autoComplete="name" testId="ital-final-name" />
                    <LuxField icon={Mail} type="email" placeholder="Email *" value={fEmail} onChange={(e) => setFEmail(e.target.value)} autoComplete="email" testId="ital-final-email" />
                    <LuxField icon={Phone} type="tel" placeholder="Telefono (consigliato)" value={fPhone} onChange={(e) => setFPhone(e.target.value)} autoComplete="tel" testId="ital-final-phone" />
                    <button
                      type="submit"
                      disabled={fName.trim().length < 2 || !emailRe.test(fEmail.trim()) || fSending}
                      className="ital-gold-btn w-full rounded-full min-h-12 py-2.5 px-4 text-[11px] sm:text-[13px] font-bold tracking-[0.12em] sm:tracking-[0.2em] uppercase text-center leading-snug inline-flex items-center justify-center gap-2"
                      data-testid="ital-final-submit"
                    >
                      {fSending ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {territory === 'TH' ? "Entra in lista d'attesa" : 'Ricevi il dossier'} <ArrowRight className="w-4 h-4" />
                    </button>
                    <a href={calendly} target="_blank" rel="noopener noreferrer" data-testid="ital-final-booking">
                      <span className="mt-1 w-full rounded-full min-h-12 py-2.5 px-4 text-[11px] sm:text-[12px] font-bold tracking-[0.12em] sm:tracking-[0.2em] uppercase text-center leading-snug inline-flex items-center justify-center gap-2 border transition-colors duration-150 hover:bg-white/5" style={{ borderColor: GOLD, color: IVORY }}>
                        Prenota una consulenza strategica col CEO <ArrowRight className="w-4 h-4" />
                      </span>
                    </a>
                    {/* ★ Fase 96 (Doc 02) — microcopy completa */}
                    <NoObligationNote lang="it" theme="dark" color={IVORY} iconColor={GOLD} className="pt-1" testId="ital-final-no-obligation" />
                  </form>
                )}
              </div>
            </div>
          </Scene>

      {/* ── Footer ── */}
      <footer className="relative border-t" style={{ background: `${INK}D9`, backdropFilter: 'blur(10px)', borderColor: `${GOLD}26` }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div>
            <img src="/funnels/hcf-logo.png" alt="HCF Property" className="h-8" />
            <p className="text-[11px] font-sans mt-2" style={{ color: SAND }}>© {new Date().getFullYear()} HCF Property</p>
          </div>
          <div className="flex gap-5 text-xs font-sans items-center" style={{ color: SAND }}>
            <Link to="/privacy" className="underline hover:opacity-80" data-testid="ital-footer-privacy">Privacy</Link>
            <Link to="/terms" className="underline hover:opacity-80" data-testid="ital-footer-terms">Termini</Link>
            <Link to="/cookie-policy" className="underline hover:opacity-80" data-testid="ital-footer-cookies">Cookie</Link>
            <span className="tracking-[0.25em]" data-testid="ital-footer-code">IT</span>
          </div>
        </div>
      </footer>

      {/* ★ Fase 47 — exit intent: ultimo invito con Calendly */}
      <ExitIntentModal calendly={calendly} />

      {/* ★ Fase 44 — Chat dedicata del funnel IT-AL (knowledge base = questa landing)
          ★ Fase 45 — quando il cliente è caldo la chat mostra il bottone
          «Prenota la call» → calendario con Richard Demichele preselezionato. */}
      <FunnelChatWidget funnel="it" language="it" bookingUrl="/it/booking?rep=richard" />
    </div>
  );
}
